import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, ChevronRight, Rocket, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { getSetupStatus, type SetupStatus } from '@/lib/api';
import { cn } from '@/lib/utils';

type StepKey = Exclude<keyof SetupStatus, 'account_age_days'>;
/** `optional` steps are listed but never keep the checklist open */
type Step = { key: StepKey; title: string; desc: string; to: string; show: boolean; optional?: boolean };

/** Only people new to the app see the checklist: accounts younger than this */
const NEW_ACCOUNT_DAYS = 30;

const dismissKey = (userId: string) => `getting-started-dismissed:${userId}`;

function isDismissed(userId: string) {
  try { return localStorage.getItem(dismissKey(userId)) === '1'; } catch { return false; }
}

/**
 * "Getting Started" checklist for people new to the app (account < NEW_ACCOUNT_DAYS old):
 * a new clinic's owner, or a doctor who just joined. Each step is derived from real data
 * (GET /settings/setup-status) and links straight to the screen that completes it.
 * Only pending steps are listed. Hides itself once every required step is done, after the
 * first month, or when dismissed — and then renders `fallback` instead (Home passes the
 * feature-tips card), so only one card shows at a time.
 */
export default function GettingStartedCard({ compact = false, fallback = null }: { compact?: boolean; fallback?: ReactNode }) {
  const { currentUser } = useAuth();
  const { hasFeature } = usePermission();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [dismissed, setDismissed] = useState(() => !!currentUser && isDismissed(currentUser.id));

  const isAdmin = currentUser?.role === 'admin';
  const isDoctor = !!currentUser?.doctor_id;
  const visible = !!currentUser && (isAdmin || isDoctor) && !dismissed;

  // Refetch on navigation so steps tick off after the user saves something and moves on
  useEffect(() => {
    if (!visible) return;
    getSetupStatus().then(setStatus).catch(() => {});
  }, [visible, location.key]);

  if (!visible) return <>{fallback}</>;
  if (!status) return null;
  if (status.account_age_days > NEW_ACCOUNT_DAYS) return <>{fallback}</>;

  const roleAccess = hasFeature('role_access');
  const steps: Step[] = ([
    { key: 'clinic_info',         title: 'Add clinic address & phone',   desc: 'Printed on prescriptions and invoices',        to: '/settings?section=clinic',              show: isAdmin },
    { key: 'doctor_added',        title: 'Add a doctor',                 desc: 'Each doctor gets their own login',             to: '/settings?section=doctors',             show: isAdmin },
    { key: 'doctor_profile',      title: 'Complete your doctor profile', desc: 'License no. and signature for prescriptions',  to: '/settings?section=profile',             show: isDoctor },
    { key: 'prescription_layout', title: 'Review prescription layout',   desc: 'Hide sections you don\'t use, add custom fields', to: '/settings?section=prescription-layout', show: isDoctor, optional: true },
    { key: 'billing_services',    title: 'Set up consultation fees',     desc: 'Services and prices used when billing',        to: '/settings?section=billing',             show: isAdmin },
    { key: 'staff_added',         title: 'Invite front-desk staff',      desc: 'For registration and the queue',               to: '/settings?section=staff',               show: isAdmin && roleAccess, optional: true },
    { key: 'first_patient',       title: 'Register your first patient',  desc: 'Name and phone are enough to start',           to: isDoctor ? '/patients' : '/frontdesk/patients/new',         show: true },
    { key: 'first_appointment',   title: 'Book the first appointment',   desc: 'Or add a walk-in to today\'s queue',           to: isDoctor ? '/appointments' : '/frontdesk/appointments/new', show: true },
    { key: 'first_prescription',  title: 'Write your first prescription', desc: 'Pick a patient and start a consultation',     to: '/prescriptions',                        show: isDoctor },
  ] as Step[]).filter(s => s.show && status[s.key] !== null);

  const required = steps.filter(s => !s.optional);
  const doneCount = required.filter(s => status[s.key]).length;
  if (doneCount === required.length) return <>{fallback}</>;
  // Pending only, required first; finished steps collapse into a single "N done" line
  const pending = [...required, ...steps.filter(s => s.optional)].filter(s => !status[s.key]);
  const next = pending[0];
  const nextKey = next.key;

  const dismiss = () => {
    try { localStorage.setItem(dismissKey(currentUser!.id), '1'); } catch { /* storage unavailable */ }
    setDismissed(true);
  };

  // One-line strip for screens where the full card would push the real content off-screen
  if (compact) {
    return (
      <div className="flex items-center gap-3 mb-4 px-3 py-2 rounded-lg border border-primary/20 bg-primary/5 text-sm">
        <Rocket className="w-4 h-4 text-primary shrink-0" />
        <span className="text-muted-foreground shrink-0">Getting started: {doneCount}/{required.length}</span>
        <button onClick={() => navigate(next.to)} className="flex-1 min-w-0 text-left truncate font-medium text-primary hover:underline">
          Next: {next.title}
        </button>
        <button onClick={() => navigate('/')} className="text-xs text-muted-foreground hover:text-foreground shrink-0">View all</button>
        <button onClick={dismiss} className="p-0.5 rounded text-muted-foreground hover:bg-muted hover:text-foreground shrink-0" title="Hide checklist">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <Card className="medical-card mb-6">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Rocket className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Getting started</p>
              <p className="text-xs text-muted-foreground">{doneCount} of {required.length} done — finish these to get started</p>
            </div>
          </div>
          <button onClick={dismiss} className="p-1 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground" title="Hide checklist">
            <X className="w-4 h-4" />
          </button>
        </div>

        <Progress value={(doneCount / required.length) * 100} className="h-1.5 mb-3" />

        <div className="grid gap-1 sm:grid-cols-2">
          {pending.map(s => (
            <button key={s.key} onClick={() => navigate(s.to)}
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors hover:bg-muted',
                s.key === nextKey && 'bg-primary/5 ring-1 ring-primary/20',
              )}>
              <Circle className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {s.title}
                  {s.optional && <span className="ml-1.5 text-2xs font-normal text-muted-foreground">Optional</span>}
                </p>
                <p className="text-xs text-muted-foreground truncate">{s.desc}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>

        {steps.length > pending.length && (
          <p className="flex items-center gap-1.5 mt-2 px-3 text-xs text-muted-foreground">
            <CheckCircle2 className="w-3.5 h-3.5 text-success" /> {steps.length - pending.length} done
          </p>
        )}
      </CardContent>
    </Card>
  );
}
